import React, { Fragment, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import Header from '../../components/Header';
import Load from '../../components/Load';
import CreditCardModal, { CreditCardInfo } from '../../components/CreditCard';
import PixModal from '../../components/PixModal';
import { useAuth } from '../../context/AuthContext';
import {
  finalizarPagamentoAgendamento,
  MeioPagamento,
  AgendamentoPagamentoResult,
} from '../../services/agendamentoPagamentoService';
import { buscarEnderecoPorCep } from '../../services/cepService';

const AGENDAMENTO_PAGAMENTO_QUERY = gql`
  query AgendamentoPorIdPagamento($id: ID!) {
    agendamentoPorId(id: $id) {
      id
      dataAgendamento
      horaInicio
      horaFim
      status
      valor
      invoiceId
      meioPagamento
      quadra {
        id
        nome
        local {
          id
          nome
          aceitaPix
          aceitaCartao
        }
      }
      usuario {
        id
        nome
      }
    }
  }
`;

const MEU_SALDO_NO_LOCAL_AG = gql`
  query MeuSaldoNoLocalAg($localId: ID!) {
    meuSaldoNoLocal(localId: $localId)
  }
`;

const ATLETA_ENDERECO_QUERY = gql`
  query AtletaEnderecoParaPagamento($id: ID!) {
    atletaPorId(id: $id) {
      id
      cidade {
        nome
        uf
      }
    }
  }
`;

const schema = z.object({
  cep: z.string().regex(/^[0-9]{5}-?[0-9]{3}$/, 'CEP inválido'),
  estado: z.string().min(1, 'Campo obrigatório'),
  cidade: z.string().min(1, 'Campo obrigatório'),
  bairro: z.string().min(1, 'Campo obrigatório'),
  endereco: z.string().min(1, 'Campo obrigatório'),
  numero: z.string().min(1, 'Campo obrigatório'),
  complemento: z.string().optional(),
  paymentMethod: z.enum(['pix', 'credit'], {
    required_error: 'Selecione a forma de pagamento',
  }),
});

type FormData = z.infer<typeof schema>;

const PagamentoAgendamento: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const auth = useAuth();
  const atletaId = (auth?.user as { atleta?: { id?: string } } | null)?.atleta?.id;

  const { data, loading, error } = useQuery(AGENDAMENTO_PAGAMENTO_QUERY, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'network-only',
  });

  const { data: atletaData } = useQuery(ATLETA_ENDERECO_QUERY, {
    variables: { id: atletaId },
    skip: !atletaId,
  });

  const agLocalId = data?.agendamentoPorId?.quadra?.local?.id;
  const { data: saldoData } = useQuery(MEU_SALDO_NO_LOCAL_AG, {
    variables: { localId: agLocalId },
    skip: !agLocalId,
    fetchPolicy: 'network-only',
  });
  const saldoCredito: number = saldoData?.meuSaldoNoLocal ?? 0;

  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixCode, setPixCode] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>(undefined);
  const [isPaying, setIsPaying] = useState(false);
  const [formValues, setFormValues] = useState<FormData | null>(null);
  const [erroMsg, setErroMsg] = useState<string | null>(null);
  const [sucesso, setSucesso] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  // Pré-preencher cidade/estado do perfil do atleta OU endereço salvo anteriormente
  useEffect(() => {
    const saved = localStorage.getItem('inplay_endereco_cobranca');
    if (saved) {
      try {
        const end = JSON.parse(saved);
        if (end.cep) setValue('cep', end.cep);
        if (end.estado) setValue('estado', end.estado);
        if (end.cidade) setValue('cidade', end.cidade);
        if (end.bairro) setValue('bairro', end.bairro);
        if (end.endereco) setValue('endereco', end.endereco);
        if (end.numero) setValue('numero', end.numero);
        if (end.complemento) setValue('complemento', end.complemento);
      } catch { /* ignora JSON inválido */ }
    } else {
      const cidade = atletaData?.atletaPorId?.cidade;
      if (cidade) {
        setValue('cidade', cidade.nome || '');
        setValue('estado', cidade.uf || '');
      }
    }
  }, [atletaData, setValue]);

  // Busca endereço pelo CEP via ViaCEP
  const [cepErro, setCepErro] = useState<string | null>(null);

  const buscarCep = async () => {
    const cepValue = watch('cep');
    setCepErro(null);
    if (!cepValue) {
      setCepErro('Informe o CEP');
      return;
    }
    const cepLimpo = cepValue.replace(/\D/g, '');
    if (cepLimpo.length !== 8) {
      setCepErro('CEP deve ter 8 dígitos');
      return;
    }

    setBuscandoCep(true);
    try {
      const endereco = await buscarEnderecoPorCep(cepValue);
      setValue('endereco', endereco.logradouro || '');
      setValue('bairro', endereco.bairro || '');
      setValue('cidade', endereco.localidade || '');
      setValue('estado', endereco.uf || '');
    } catch (err: any) {
      setCepErro(err.message === 'CEP não encontrado' ? err.message : 'Erro ao buscar CEP. Verifique e tente novamente.');
    } finally {
      setBuscandoCep(false);
    }
  };

  const paymentMethod = watch('paymentMethod');

  if (loading) return <Load />;
  if (error) return (
    <div className="container mt-5">
      <div className="alert alert-danger">{error.graphQLErrors?.[0]?.message ?? error.message}</div>
    </div>
  );

  const agendamento = data?.agendamentoPorId;
  if (!agendamento) return (
    <div className="container mt-5">
      <div className="alert alert-warning">Agendamento não encontrado.</div>
    </div>
  );

  const local = agendamento.quadra?.local;
  const aceitaPix = local?.aceitaPix !== false;
  const aceitaCartao = local?.aceitaCartao !== false;
  const podePagar = ['RESERVADO', 'A_PAGAR'].includes(agendamento.status);
  const valorDefinido = agendamento.valor != null && agendamento.valor > 0;

  if (!podePagar) {
    return (
      <Fragment>
        <Header />
        <div className="main-content">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left pe-0">
              <div className="container pt-4">
                <div className="alert alert-info">
                  Este agendamento já está com status <strong>{agendamento.status}</strong> e não pode receber pagamento.
                </div>
                <button className="btn btn-primary" onClick={() => navigate('/quadras/meus-agendamentos')}>
                  Voltar para Meus Agendamentos
                </button>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  if (!valorDefinido) {
    return (
      <Fragment>
        <Header />
        <div className="main-content">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left pe-0">
              <div className="container pt-4">
                <div className="alert alert-warning">
                  <i className="feather-alert-circle me-2"></i>
                  Este agendamento ainda não possui valor definido. Entre em contato com o administrador do local.
                </div>
                <button className="btn btn-primary rounded-xl" onClick={() => navigate('/quadras/meus-agendamentos')}>
                  Voltar para Meus Agendamentos
                </button>
              </div>
            </div>
          </div>
        </div>
      </Fragment>
    );
  }

  const mapEndereco = (form: FormData) => ({
    cep: form.cep,
    logradouro: form.endereco,
    numero: form.numero,
    bairro: form.bairro,
    cidade: form.cidade,
    uf: form.estado,
    pais: 'BR',
    complemento: form.complemento || undefined,
  });

  const onValid = async (form: FormData) => {
    setFormValues(form);
    setErroMsg(null);

    // Salvar endereço para pré-preencher em futuros pagamentos
    localStorage.setItem('inplay_endereco_cobranca', JSON.stringify({
      cep: form.cep, estado: form.estado, cidade: form.cidade,
      bairro: form.bairro, endereco: form.endereco, numero: form.numero,
      complemento: form.complemento || '',
    }));

    if (form.paymentMethod === 'credit') {
      setShowCreditModal(true);
      return;
    }

    if (form.paymentMethod === 'pix') {
      setIsPaying(true);
      try {
        const result = await finalizarPagamentoAgendamento(agendamento.id, {
          meioPagamento: 'PIX' as MeioPagamento,
          enderecoCobranca: mapEndereco(form),
        });

        if (result.pixInfo) {
          const code = result.pixInfo.copyAndPasteKey || result.pixInfo.qrcodeText || '';
          const qr = result.pixInfo.qrcodeImage || undefined;

          if (code || qr) {
            // Cenário ideal: veio QR code / código PIX — mostra modal na própria tela
            setPixCode(code);
            setQrCodeUrl(qr);
            setShowPixModal(true);
          } else if (result.pixInfo.secureUrl) {
            // Fallback (ambiente de teste): não veio QR mas veio URL de checkout
            // Mostra o código na modal com a URL como fallback
            setPixCode(result.pixInfo.secureUrl);
            setQrCodeUrl(undefined);
            setShowPixModal(true);
          } else {
            setErroMsg('Não foi possível gerar os dados PIX. Tente novamente.');
          }
        } else {
          setErroMsg('Não foi possível gerar os dados PIX. Tente novamente.');
        }
      } catch (e: any) {
        const raw = e?.graphQLErrors?.[0]?.message ?? e?.message ?? 'Erro ao gerar PIX.';
        setErroMsg(typeof raw === 'string' ? raw : String(raw));
      } finally {
        setIsPaying(false);
      }
    }
  };

  const handleCreditPay = async (cardData: CreditCardInfo) => {
    if (!formValues) return;
    setIsPaying(true);
    setErroMsg(null);
    try {
      const result = await finalizarPagamentoAgendamento(agendamento.id, {
        meioPagamento: 'CARTAO' as MeioPagamento,
        enderecoCobranca: mapEndereco(formValues),
        dadosCartao: {
          numero: cardData.cardNumber,
          nomeImpresso: cardData.holderName,
          validadeMes: Number(cardData.expiryMonth),
          validadeAno: Number(cardData.expiryYear) ? 2000 + Number(cardData.expiryYear) : 0,
          cvv: cardData.securityCode,
        },
      });

      if (result.confirmado) {
        setSucesso(true);
        setShowCreditModal(false);
        setTimeout(() => navigate('/quadras/meus-agendamentos'), 2000);
      }
    } catch (e: any) {
      const raw = e?.graphQLErrors?.[0]?.message ?? e?.message ?? 'Erro ao processar pagamento.';
      setErroMsg(typeof raw === 'string' ? raw : String(raw));
      setShowCreditModal(false);
    } finally {
      setIsPaying(false);
    }
  };

  const formatCurrency = (val: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">
            <div className="container pt-4" style={{ maxWidth: '700px' }}>

              {/* Resumo do agendamento */}
              <div className="card border-0 shadow-xss p-4 mb-3">
                <h4 className="fw-700 font-xs text-grey-900 mb-3">
                  <i className="bi bi-calendar-check text-primary me-2"></i>
                  Pagamento do Agendamento
                </h4>
                <div className="d-flex flex-column gap-2">
                  <div className="d-flex justify-content-between">
                    <span className="text-grey-600">Quadra:</span>
                    <span className="fw-600">{agendamento.quadra?.nome}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-grey-600">Local:</span>
                    <span className="fw-600">{local?.nome}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-grey-600">Data:</span>
                    <span className="fw-600">{agendamento.dataAgendamento}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-grey-600">Horário:</span>
                    <span className="fw-600">{agendamento.horaInicio} - {agendamento.horaFim}</span>
                  </div>
                  <hr className="my-2" />
                  {saldoCredito > 0 && agendamento.valor > 0 && (
                    <div className="d-flex justify-content-between mb-1">
                      <span className="font-xssss text-success fw-600">
                        <i className="bi bi-wallet2 me-1"></i>Crédito neste local
                      </span>
                      <span className="font-xssss text-success fw-600">-{formatCurrency(Math.min(saldoCredito, agendamento.valor))}</span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between">
                    <span className="fw-700 font-xss">Total a pagar:</span>
                    <span className="fw-700 font-xss text-primary">{formatCurrency(Math.max(0, (agendamento.valor ?? 0) - (agendamento.valor > 0 ? saldoCredito : 0)))}</span>
                  </div>
                  {saldoCredito > 0 && agendamento.valor > 0 && saldoCredito >= agendamento.valor && (
                    <div className="alert alert-success font-xssss py-2 mt-2 mb-0">
                      <i className="bi bi-check-circle me-1"></i>
                      Seu crédito cobre o valor total. O pagamento será confirmado automaticamente.
                    </div>
                  )}
                </div>
              </div>

              {sucesso && (
                <div className="alert alert-success">
                  <i className="bi bi-check-circle me-2"></i>
                  Pagamento realizado com sucesso! Redirecionando...
                </div>
              )}

              {erroMsg && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {erroMsg.includes('CPF') ? (
                    <>
                      CPF não cadastrado.{' '}
                      <a href="/athlete/profile" className="fw-bold text-decoration-underline">
                        Clique aqui para atualizar seu CPF
                      </a>
                    </>
                  ) : erroMsg}
                </div>
              )}

              {!sucesso && (
                <form onSubmit={handleSubmit(onValid)}>
                  {/* Endereço de cobrança */}
                  <div className="card border-0 shadow-xss p-4 mb-3">
                    <h5 className="fw-700 font-xss text-grey-900 mb-3">Endereço de cobrança</h5>
                    <div className="row">
                      <div className="col-md-4 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">CEP *</label>
                        <div className="d-flex gap-1">
                          <input type="text" className="form-control" placeholder="88888-888" {...register('cep')} 
                            onBlur={buscarCep}
                            onChange={(e) => { setCepErro(null); register('cep').onChange(e); }}
                          />
                          <button type="button" className="btn btn-light btn-sm" onClick={buscarCep} disabled={buscandoCep}
                            title="Buscar endereço pelo CEP">
                            {buscandoCep ? <i className="bi bi-hourglass-split"></i> : <i className="bi bi-search"></i>}
                          </button>
                        </div>
                        {errors.cep && <small className="text-danger">{errors.cep.message}</small>}
                        {!errors.cep && cepErro && <small className="text-danger">{cepErro}</small>}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Estado *</label>
                        <input type="text" className="form-control" placeholder="SC" {...register('estado')} />
                        {errors.estado && <small className="text-danger">{errors.estado.message}</small>}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Cidade *</label>
                        <input type="text" className="form-control" {...register('cidade')} />
                        {errors.cidade && <small className="text-danger">{errors.cidade.message}</small>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Bairro *</label>
                        <input type="text" className="form-control" {...register('bairro')} />
                        {errors.bairro && <small className="text-danger">{errors.bairro.message}</small>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Endereço *</label>
                        <input type="text" className="form-control" {...register('endereco')} />
                        {errors.endereco && <small className="text-danger">{errors.endereco.message}</small>}
                      </div>
                      <div className="col-md-4 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Número *</label>
                        <input type="text" className="form-control" {...register('numero')} />
                        {errors.numero && <small className="text-danger">{errors.numero.message}</small>}
                      </div>
                      <div className="col-md-8 mb-3">
                        <label className="font-xssss fw-600 text-grey-700">Complemento</label>
                        <input type="text" className="form-control" {...register('complemento')} />
                      </div>
                    </div>
                  </div>

                  {/* Forma de pagamento */}
                  <div className="card border-0 shadow-xss p-4 mb-3">
                    <h5 className="fw-700 font-xss text-grey-900 mb-3">Forma de pagamento</h5>
                    <div className="d-flex flex-column gap-2">
                      {aceitaPix && (
                        <label className="d-flex align-items-center gap-2 p-3 border rounded cursor-pointer">
                          <input type="radio" value="pix" {...register('paymentMethod')} />
                          <i className="bi bi-qr-code fs-5 text-primary"></i>
                          <span className="fw-600">PIX</span>
                          <span className="text-grey-500 font-xssss ms-auto">Aprovação imediata</span>
                        </label>
                      )}
                      {aceitaCartao && (
                        <label className="d-flex align-items-center gap-2 p-3 border rounded cursor-pointer">
                          <input type="radio" value="credit" {...register('paymentMethod')} />
                          <i className="bi bi-credit-card fs-5 text-primary"></i>
                          <span className="fw-600">Cartão de Crédito</span>
                          <span className="text-grey-500 font-xssss ms-auto">Aprovação imediata</span>
                        </label>
                      )}
                    </div>
                    {errors.paymentMethod && (
                      <small className="text-danger mt-2 d-block">{errors.paymentMethod.message}</small>
                    )}
                  </div>

                  {/* Botões */}
                  <div className="d-flex gap-3 mb-4">
                    <button
                      type="button"
                      className="btn btn-light rounded-xl fw-600 flex-grow-1"
                      onClick={() => navigate(-1)}
                    >
                      Voltar
                    </button>
                    <button
                      type="submit"
                      className="btn bg-primary-gradiant text-white rounded-xl fw-600 flex-grow-1"
                      disabled={isPaying}
                    >
                      {isPaying ? 'Processando...' : saldoCredito >= agendamento.valor ? 'Confirmar com crédito' : 'Pagar ' + formatCurrency(Math.max(0, agendamento.valor - saldoCredito))}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Cartão de Crédito */}
      <CreditCardModal
        open={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onPay={handleCreditPay}
      />

      {/* Modal PIX */}
      <PixModal
        open={showPixModal}
        onClose={() => {
          setShowPixModal(false);
          navigate('/quadras/meus-agendamentos');
        }}
        pixCode={pixCode}
        qrCodeUrl={qrCodeUrl}
      />
    </Fragment>
  );
};

export default PagamentoAgendamento;
